import React, { useState } from 'react';
import { X, Loader2, UserPlus, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { API, errText } from '../api';

const STYLES = ['Soul', 'R&B', 'Gospel', 'Pop', 'Hip-Hop', 'Acoustic', 'Country', 'Jazz'];
const EMPTY = { owner_name: '', business_name: '', email: '', phone: '', website: '', industry: '', notes: '', style: '' };

export default function AddCustomerModal({ customer, onClose, onCreated }) {
  const [f, setF] = useState({ ...EMPTY, ...(customer ? { owner_name: customer.owner_name || '', business_name: customer.business_name || '', email: customer.email, phone: customer.phone || '' } : {}) });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const input = 'w-full rounded-xl border hairline bg-panel2/50 px-4 py-3 text-cream placeholder:text-cream/35 outline-none focus:border-gold/50 transition-colors';

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await API.createCustomer(f);
      toast.success(res.account_created ? `Account created for ${f.business_name}.` : `New song added for ${f.business_name}.`, {
        description: res.account_created ? `Login details emailed to ${f.email}. ${res.request.code} is in your inbox.` : `${res.request.code} is in your inbox.`,
      });
      onCreated(res);
    } catch (err) { toast.error(errText(err, 'Could not create the customer.')); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 grid place-items-center px-4 overflow-y-auto py-6">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <form onSubmit={submit} className="relative panel rounded-3xl p-7 w-full max-w-lg" data-testid="add-customer-modal">
        <div className="flex items-start justify-between mb-6">
          <div>
            <p className="eyebrow text-[9px] mb-1">{customer ? 'Existing customer' : 'New customer'}</p>
            <h2 className="font-display text-3xl text-cream">{customer ? 'New song for' : 'Add a'} <span className="italic text-gold-grad">{customer ? customer.business_name : 'customer.'}</span></h2>
            <p className="text-sm text-cream/55 mt-2">{customer ? 'A fresh request lands in your inbox — upload versions and send when ready.' : 'Creates their account, emails them a username and temporary password, and puts a song request in your inbox.'}</p>
          </div>
          <button type="button" onClick={onClose} data-testid="add-customer-close" className="grid place-items-center w-9 h-9 rounded-full border hairline text-cream/60 hover:text-cream shrink-0"><X size={16} /></button>
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <input required placeholder="Owner name" value={f.owner_name} onChange={set('owner_name')} data-testid="cust-owner" className={input} />
          <input required placeholder="Business name" value={f.business_name} onChange={set('business_name')} data-testid="cust-business" className={input} />
          <input required type="email" placeholder="Email (their username)" value={f.email} onChange={set('email')} readOnly={!!customer} data-testid="cust-email" className={`${input} ${customer ? 'opacity-60' : ''}`} />
          <input placeholder="Phone" value={f.phone} onChange={set('phone')} data-testid="cust-phone" className={input} />
          <input placeholder="Website (optional — AI fills the profile)" value={f.website} onChange={set('website')} data-testid="cust-website" className={`${input} sm:col-span-2`} />
          <input placeholder="Industry (e.g. Chiropractic)" value={f.industry} onChange={set('industry')} data-testid="cust-industry" className={input} />
          <select value={f.style} onChange={set('style')} data-testid="cust-style" className={input}>
            <option value="">Style — you decide</option>
            {STYLES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <textarea rows={3} placeholder="Notes for the song (what they do, who they serve, anything to sing about)" value={f.notes} onChange={set('notes')} data-testid="cust-notes" className={`${input} sm:col-span-2 resize-none`} />
        </div>
        {f.website && <p className="mt-3 text-xs text-cream/45 flex items-center gap-2"><Sparkles size={12} className="text-gold" /> The AI reader will read their site and draft the brief. Takes a few seconds.</p>}
        <button type="submit" disabled={busy} data-testid="cust-submit" className="mt-6 w-full btn-gold rounded-full py-3.5 flex items-center justify-center gap-2 disabled:opacity-70">
          {busy ? <Loader2 size={16} className="animate-spin" /> : <><UserPlus size={16} /> {customer ? 'Create song request' : 'Create account and song request'}</>}
        </button>
      </form>
    </div>
  );
}
